import { Component, createRef, Fragment } from 'react';

import { IOC_TYPES } from '../../../context/ioc.types';
import { container, lazyInject } from '../../../context/module';
import { FileUpload } from '../../../models/file-upload';
import { Logger } from '../../../models/logger';
import { UIContext } from '../../../services/ui-context';
import {
    ConvertOption,
    DropdownOption,
    DropdownWidget,
    GenericWidget,
    InputDropdownWidget,
    ISeekBarWidget,
} from '../../../types/dropdown.type';
import { StringUtils } from '../../../utils/string-utils';
import Dropdown from '../../ui/dropdown/dropdown';
import InputWidgetComponent from '../../ui/input-widget/input-widget.component';
import InputWithDropdownComponent from '../../ui/input-with-dropdown/input-with-dropdown.component';
import Seekbar from '../../ui/seekbar/seekbar.component';
import { ConvertOptionsContext } from '../convert-options-context';
import { isFunction } from '../../../utils/lodash-min';
import styles from './html-widget.component.module.scss';
import { WidgetNotesComponent } from './widget-helpers/widget-notes.component';
import Checkbox from '../../ui/checkbox/checkbox.component';


interface HTMLWidgetComponentProps {
    formGroup: ConvertOption;
    convertOptionsContext: ConvertOptionsContext;
    data?: Array<DropdownOption>;
    fileUpload?: FileUpload;
    select: (data: any) => void;
}

interface HTMLWidgetComponentState {
    selectedOption?: any;
    childKeyPrefix?: string;
}

export default class HTMLWidgetComponent extends Component<HTMLWidgetComponentProps, HTMLWidgetComponentState> {
    logger = Logger.getLogger('HTMLWidgetComponent');
    jsonConvertableWidget = createRef();

    @lazyInject(IOC_TYPES.UIContext) uiContext: UIContext;

    constructor(props: HTMLWidgetComponentProps) {
        super(props);
    }

    getSelected() {
        return this.props.data.find(item => item.selected) || this.props.data[0];
    }

    onClick(event: Event, item) {
        event.preventDefault();
        this.props.data.forEach(_item => _item.selected = false);
        this.props.data.find(_item => item.formValue === _item.formValue).selected = true;
        this.props.select(this.getSelected());
    }

    drowdownSelected(parent: DropdownOption[], value: DropdownOption) {
        parent.forEach(i => {
            if (i.formName === value.formName) {
                i.selected = true;
            } else {
                i.selected = false;
            }
        });
    }

    getSelectedFormValue(item: DropdownOption[]) {
        return item.find(i => i.selected).formDisplayValue;
    }

    isRegularDropdown(typeLessFormGroup) {
        if (typeLessFormGroup && typeLessFormGroup.selections) {
            if (Array.isArray(typeLessFormGroup.selections) && typeLessFormGroup.selections.length > 0) {
                return !typeLessFormGroup.selections[0].displayAs && !this.isParentOrChildDropdown(typeLessFormGroup);
            }
        }
        return false;
    }

    isParentOrChildDropdown(typeLessFormGroup) {
        if (typeLessFormGroup && typeLessFormGroup.selections) {
            if (Array.isArray(typeLessFormGroup.selections) && typeLessFormGroup.selections.length > 0) {
                const fn = (selection, expectedDisplayAs) => {
                    return !!(selection || []).find(s => s.displayAs === expectedDisplayAs);
                }
                return fn(typeLessFormGroup.selections, 'parent')
                    || fn(typeLessFormGroup.selections, 'leaf');
            }
        }
        return false;
    }

    isChildDropdown(typeLessFormGroup) {
        if (typeLessFormGroup && typeLessFormGroup.selections) {
            return (typeLessFormGroup.displayAs === 'dropdown' &&
                typeLessFormGroup.type === 'dropdown');
        }
        return false;
    }

    isSeekbar(typeLessFormGroup) {
        if (typeLessFormGroup && typeLessFormGroup.selections) {
            return (typeLessFormGroup.displayAs === 'seekbar'
                && typeLessFormGroup.type === 'dropdown');
        }
        return false;
    }

    isCheckbox(typeLessFormGroup) {
        if (typeLessFormGroup && typeLessFormGroup.selections) {
            return (typeLessFormGroup.displayAs === 'checkbox'
                && typeLessFormGroup.type === 'dropdown');
        }
        return false;
    }

    isInputDropdown(typeLessFormGroup) {
        if (typeLessFormGroup && typeLessFormGroup.formGroup) {
            return (typeLessFormGroup.displayAs === 'input_dropdown'
                && typeLessFormGroup.type === 'input_dropdown');
        }
        return false;
    }

    isInputWidget(typeLessFormGroup) {
        if (typeLessFormGroup) {
            return (typeLessFormGroup.displayAs === 'input'
                && typeLessFormGroup.type === 'dropdown');
        }
        return false;
    }

    getSelectedItemFromSelections(selections) {
        return selections.find(s => s.selected);
    }

    setDropdownValue(selectedValue: DropdownOption, forced: boolean, formChild: DropdownWidget) {
        formChild.selections.forEach(item => item.selected = false);
        if (selectedValue) {
            selectedValue.selected = true;
        } else if (forced) {
            this.logger.error(`Dropdown forced to select a value but there was no selectable values available. 
                This could happen when previously drodown option change to a number input`);
        }
        this.props.convertOptionsContext.emitEvent({
            formChild: formChild,
            selectedValue: selectedValue,
        });
    }

    setSeekbarValue(value, formChild: GenericWidget<ISeekBarWidget>) {
        formChild.options.value = value;
        if ((formChild as any).selections) {
            (formChild as any).selections.forEach(item => item.selected = false);
            const selectedValue = (formChild as any).selections.find(item => item.formValue === `${value}`);
            if (selectedValue) {
                selectedValue.selected = true;
            }
            this.props.convertOptionsContext.emitEvent({
                formChild: formChild,
                selectedValue: selectedValue,
            });
        }
    }

    setCheckboxValue(value, formChild: GenericWidget<ISeekBarWidget>) {
        formChild.options.value = value;
        if ((formChild as any).selections) {
            (formChild as any).selections.forEach(item => item.selected = false);
            const selectedValue = (formChild as any).selections.find(item => item.formValue === `${value}`);
            if (selectedValue) {
                selectedValue.selected = true;
            }
            this.props.convertOptionsContext.emitEvent({
                formChild: formChild,
                selectedValue: selectedValue,
            });
        }
    }

    setInputWithDropdownValue(value: { dropdown: any, value: number }, formChild: InputDropdownWidget, dropdownForm: DropdownWidget) {
        dropdownForm.selections.forEach(item => item.selected = false);
        if (value.dropdown) {
            value.dropdown.selected = true;
        }
        formChild.formValue = value;
        const summary = {
            formChild: formChild,
            selectedValue: formChild.formValue,
        };
        this.logger.trace1({ message: 'setInputWithDropdownValue', data: summary });
        this.props.convertOptionsContext.emitEvent(summary);
    }

    setInputValue(value: number | string, formChild: InputDropdownWidget, dropdownForm: DropdownWidget) {
        formChild.formValue = `${value}`;
        this.props.convertOptionsContext.emitEvent({
            formChild: formChild,
            selectedValue: formChild.formValue,
        });
    }

    async canSelect(selectedValue) {
        if (selectedValue?.featureFlags?.canSelect?.fn) {
            const canSelect = await selectedValue.featureFlags.canSelect.fn(container, IOC_TYPES);
            if (!canSelect) {
                this.uiContext.errorToast("Option Unavailable", selectedValue.featureFlags.canSelect.errorMessage);
            }
            return canSelect;
        }
        return true;
    }

    canShowDropdownValue(typeLessFormGroup, v) {
        if (isFunction(v.shownFn)) {
            return v.shownFn(this.props.convertOptionsContext.getConvertOptionCategories());
        }
        return true;
    }

    setAndGetParentDropdownValue(selectedValue: any, forced: boolean, typeLessFormGroup) {
        typeLessFormGroup.selections.forEach(item => item.selected = false);
        selectedValue.selected = true;
        this.setState({ selectedOption: selectedValue });
        if (forced) {
            setTimeout(async () => {
                const key = await StringUtils.hash(JSON.stringify(selectedValue));
                this.setState({
                    childKeyPrefix: key,
                })
            }, 10);
        }
        return selectedValue;
    }

    unknownElement(at) {
        this.logger.error('Request to render an unknown element.');
    }

    getJSX(props: HTMLWidgetComponentProps) {
        const typeLessFormGroup: any = props.formGroup;
        if (this.isRegularDropdown(typeLessFormGroup)) {
            return <div>
                <Dropdown initialValue={this.getSelectedItemFromSelections(typeLessFormGroup.selections)}
                    values={typeLessFormGroup.selections} label={typeLessFormGroup.formGroupDisplayName}
                    valueFormatter={(v) => v.formDisplayValue}
                    hasTooltip={(v) => !!v.tooltip}
                    getTooltip={(v) => v.tooltip}
                    onSelect={(selectedValue, forced) => this.setDropdownValue(selectedValue, forced, typeLessFormGroup)}
                    shownFn={(v) => this.canShowDropdownValue(typeLessFormGroup, v)}
                    canSelect={(selectedValue) => this.canSelect(selectedValue)}
                >
                </Dropdown>
            </div>;
        } else if (this.isParentOrChildDropdown(typeLessFormGroup)) {
            const getChild = () => {
                // React state was null
                if (this.state && this.state.selectedOption) {
                    return this.state.selectedOption;
                }
                const selectedValue = this.getSelectedItemFromSelections(typeLessFormGroup.selections);
                return this.setAndGetParentDropdownValue(selectedValue, false, typeLessFormGroup);
            }

            const selectedOption = getChild();
            const effectiveSelectedOption = (this.state && this.state.selectedOption) || selectedOption;
            return <div>
                {
                    <Dropdown
                        initialValue={this.getSelectedItemFromSelections(typeLessFormGroup.selections)}
                        values={typeLessFormGroup.selections}
                        label={typeLessFormGroup.formGroupDisplayName}
                        onSelect={(selectedValue, forced) => this.setAndGetParentDropdownValue(selectedValue, forced, typeLessFormGroup)}
                        valueFormatter={(v) => v.formDisplayValue}
                        hasTooltip={(v) => !!v.tooltip}
                        getTooltip={(v) => v.tooltip}
                        shownFn={(v) => this.canShowDropdownValue(typeLessFormGroup, v)}
                        emitInitialValueOnInit={true}
                    >
                    </Dropdown>
                }
                {
                    effectiveSelectedOption && effectiveSelectedOption.displayAs === 'leaf' && ''
                    // Nothing to do here
                }
                {
                    effectiveSelectedOption && effectiveSelectedOption.displayAs === 'parent' &&
                    <div key={`children`} className={`children ${styles['child-form']}`}>
                        {
                            effectiveSelectedOption.formChildren.map((formChild, index) => {
                                if (this.isChildDropdown(formChild)) {
                                    return <Fragment key={`fragment-${index}`}>
                                        <div key={`${index}`}>
                                            <Dropdown
                                                initialValue={this.getSelectedItemFromSelections(formChild.selections)}
                                                values={formChild.selections}
                                                label={formChild.formDisplayValue}
                                                valueFormatter={(v) => v.formDisplayValue}
                                                hasTooltip={(v) => !!v.tooltip}
                                                getTooltip={(v) => v.tooltip}
                                                emitInitialValueOnInit={true}
                                                shownFn={(v) => this.canShowDropdownValue(typeLessFormGroup, v)}
                                                onSelect={(selectedValue, forced) => this.setDropdownValue(selectedValue, forced, formChild)}
                                            >
                                            </Dropdown>
                                            {
                                                formChild.note && <WidgetNotesComponent convertOptionsContext={this.props.convertOptionsContext} formItem={formChild}></WidgetNotesComponent>
                                            }
                                        </div>
                                    </Fragment>
                                } else if (this.isSeekbar(formChild)) {
                                    this.setSeekbarValue(formChild.options.value, formChild);
                                    return <Fragment key={`fragment-${index}`}>
                                        <div>
                                            <Seekbar
                                                step={1}
                                                min={formChild.options.start}
                                                max={formChild.options.end}
                                                start={formChild.options.value}
                                                onChange={(value) => this.setSeekbarValue(value, formChild)}
                                                emitInitialValueOnInit={true}
                                            ></Seekbar>
                                            {
                                                formChild.note && <WidgetNotesComponent convertOptionsContext={this.props.convertOptionsContext} formItem={formChild}></WidgetNotesComponent>
                                            }
                                        </div>
                                    </Fragment>
                                }else if (this.isCheckbox(formChild)) {
                                    this.setCheckboxValue(formChild.options.value, formChild);
                                    return <Fragment key={`fragment-${index}`}>
                                        <div>
                                            <Checkbox
                                                initialValue={formChild.options.value}
                                                onChange={(value) => this.setCheckboxValue(value, formChild)}
                                                emitInitialValueOnInit={true}
                                            ></Checkbox>
                                            {
                                                formChild.note && <WidgetNotesComponent convertOptionsContext={this.props.convertOptionsContext} formItem={formChild}></WidgetNotesComponent>
                                            }
                                        </div>
                                    </Fragment>
                                } else if (this.isInputDropdown(formChild)) {
                                    const initialValue = formChild.formValue && formChild.formValue.input ?
                                        Number(formChild.formValue.input) : formChild.defaultValue;
                                    const getDisplayValueFn = (v) => {
                                        return v && v.formDisplayValue;
                                    }
                                    return <Fragment key={`fragment-${index}`}>
                                        <div>
                                            <InputWithDropdownComponent
                                                initialValue={this.getSelectedItemFromSelections(formChild.formGroup.selections)}
                                                values={formChild.formGroup.selections}
                                                shownFn={(value) => !!value}
                                                label={formChild.formDisplayValue}
                                                inputValue={initialValue}
                                                inputType={'number'}
                                                valueFormatter={(v) => getDisplayValueFn(v)}
                                                hasTooltip={(v) => !!v.tooltip}
                                                getTooltip={(v) => v.tooltip}
                                                onValueChange={(value) => this.setInputWithDropdownValue(value, formChild, formChild.formGroup)}
                                                emitInitialValueOnInit={true}
                                            >
                                            </InputWithDropdownComponent>
                                            {
                                                formChild.note && <WidgetNotesComponent convertOptionsContext={this.props.convertOptionsContext} formItem={formChild}></WidgetNotesComponent>
                                            }
                                        </div>
                                    </Fragment>
                                } else if (this.isInputWidget(formChild)) {
                                    const initialValue = formChild.formValue && formChild.formValue ?
                                        Number(formChild.formValue) : undefined;
                                    return <Fragment key={`fragment-${index}`}>
                                        <div>
                                            <InputWidgetComponent
                                                label={formChild.formDisplayValue}
                                                inputValue={initialValue}
                                                inputType={'number'}
                                                onValueChange={(value) => this.setInputValue(value, formChild, formChild.formGroup)}
                                                emitInitialValueOnInit={true}
                                            >
                                            </InputWidgetComponent>
                                            {
                                                formChild.note && <WidgetNotesComponent convertOptionsContext={this.props.convertOptionsContext} formItem={formChild}></WidgetNotesComponent>
                                            }
                                        </div>
                                    </Fragment>
                                }
                                return <div key={`fragment-${index}`}>
                                    <>
                                        Unknown element {this.unknownElement(formChild)}
                                    </>
                                </div>
                            })
                        }

                        {
                            this.props.formGroup.note && <div className={`${styles["description"]} alert-sm ${this.props.formGroup.note.bsTextClass}`}>
                                {this.props.formGroup.note.text}</div>
                        }
                        {
                            effectiveSelectedOption && effectiveSelectedOption.note &&
                            <div className={`${effectiveSelectedOption.note.bsTextClass} ${styles['description']}`}>
                                {
                                    effectiveSelectedOption.note.textFormatter ?
                                        effectiveSelectedOption.note.textFormatter(effectiveSelectedOption.note.text, effectiveSelectedOption)
                                        : effectiveSelectedOption.note.text
                                }
                            </div>
                        }
                    </div>
                }
            </div>
        }
    }

    render() {
        return (
            <div className={styles['html-widget']}>
                {
                    this.getJSX(this.props)
                }
            </div>
        )
    }
}
